import React, { useState, ChangeEvent, useEffect, useContext } from "react";
import { Modal, Box } from "@mui/material";
import CustomInput from "../shared/custom-input/custom-input";
import CustomButton from "../shared/custom-button/custom-button";
import { AddAPhoto as AddAPhotoIcon } from "@mui/icons-material";
import { getAllTeacherFromSuperTeacher } from "../services/super-teacher";
import { getUserGroupService } from "../services/group-service";
import { RootState } from "../redux/store/store";
import { useSelector } from "react-redux";
import { SnackbarContext } from "../config/hooks/use-toast";
import CustomAutocomplete from "../shared/custom-autoComplete/custom-autocomplete";

interface Subject {
  id?: number;
  backgroundImageUrl?: string;
  teacherId?: string;
  level: string;
  speciality: string;
  groupIds?: number[];
}

interface GroupOption {
  value: number;
  label: string;
}

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: Subject;
  defaultGroupId?: number;
  isEdit?: boolean;
  modalTitle: string;
  buttonText: string;
  onButtonClick: (formData: any) => void;
}

const defaultData: Subject = {
  level: "",
  speciality: "",
  teacherId: "",
};

const SubjectModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
  initialData = defaultData,
  defaultGroupId,
  isEdit = false,
  modalTitle,
  buttonText,
  onButtonClick,
}) => {
  const snackbarContext = useContext(SnackbarContext);

  const role = useSelector(
    (state: RootState) => state?.user?.userData?.role.name,
  );
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(
    null,
  );
  const id = useSelector((state: RootState) => state?.user?.userData?.id);
  const name = useSelector(
    (state: RootState) => state?.user?.userData?.fullName,
  );
  // mainImage removed
  const [teachersArray, setTeachers] = useState<any[]>([]);
  const [availableGroups, setAvailableGroups] = useState<GroupOption[]>([]);
  const [formData, setFormData] = useState<Subject>(initialData ?? defaultData);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(
    initialData?.groupIds && initialData.groupIds.length > 0
      ? initialData.groupIds.map(Number)
      : defaultGroupId
        ? [defaultGroupId]
        : [],
  );

  useEffect(() => {
    if (open && (role === "ROLE_ADMIN" || role === "ROLE_SUPER_TEACHER")) {
      getAllTeacherFromSuperTeacher()
        .then((res) => {
          const teachers = res.data.map((item: { fullName: any; id: any }) => ({
            label: item.fullName,
            value: item.id,
          }));
          teachers.push({
            label: name,
            value: id,
          });
          setTeachers(teachers);
        })
        .catch((e) => {
          console.log(e);
        });

      getUserGroupService()
        .then((res) => {
          const options: GroupOption[] = res.data.map((group: any) => ({
            label: group.title,
            value: group.id,
          }));
          setAvailableGroups(options);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [open, role, id, name]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.groupIds && initialData.groupIds.length > 0) {
        setSelectedGroupIds(initialData.groupIds.map(Number));
      } else if (defaultGroupId) {
        setSelectedGroupIds([defaultGroupId]);
      }
    } else {
      setFormData(defaultData);
      if (defaultGroupId) {
        setSelectedGroupIds([defaultGroupId]);
      } else {
        setSelectedGroupIds([]);
      }
    }
  }, [initialData, defaultGroupId, open]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleImageUpload = (
    event: ChangeEvent<HTMLInputElement>,
    type: "backgroundImage",
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        backgroundImageUrl: URL.createObjectURL(file),
      }));
      setBackgroundImageFile(file);
    }
  };

  // Section logic removed

  const handleGroupSelection = (groupId: number) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const handleActionClick = () => {
    const preparedFormData = new FormData();
    const PreparedJson = {
      speciality: formData.speciality,
      level: formData.level,
      // @ts-ignore
      teacherId: formData?.teacherId ? formData.teacherId : id,
    };
  
    // Check fields before stringifying
    if (PreparedJson.speciality === "" || PreparedJson.level === "") {
      if (snackbarContext) {
        snackbarContext.showMessage("Erreur", "Veuillez remplir les champs", "error");
      }
      return;
    }

    if (selectedGroupIds.length === 0) {
      if (snackbarContext) {
        snackbarContext.showMessage("Erreur", "Veuillez selectionner au moins un groupe", "error");
      }
      return;
    }
  
    const subject = JSON.stringify(PreparedJson);
    preparedFormData.append("subjectDTOJson", subject);
    console.log(subject);
  
    if (backgroundImageFile) {
      preparedFormData.append("backgroundImage", backgroundImageFile);
    }
    if (!backgroundImageFile && !formData.backgroundImageUrl) {
      if (snackbarContext) {
        snackbarContext.showMessage(
          "Erreur",
          "Veuillez importer une image de couverture",
          "error"
        );
      }
      return;
    }

    selectedGroupIds.forEach((groupIdValue) => {
      preparedFormData.append("groupIds", groupIdValue.toString());
    });
  
    onButtonClick(preparedFormData);
  
    onClose();
  };
  

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box className="bg-backgroundHome absolute top-10 left-1/2 transform -translate-x-1/2 w-11/12 sm:w-9/12 md:w-7/12 lg:w-6/12 xl:w-5/12 shadow p-5 sm:p-10 max-h-[calc(100vh-80px)] overflow-y-auto">
        <div className="flex justify-center w-full mb-5">
          <h1 className="text-3xl text-center font-montserrat_semi_bold text-primary">
            {modalTitle}
          </h1>
        </div>
        <div className="flex flex-col items-center md:flex-row justify-evenly">
          <div className="flex items-center justify-center w-1/3 h-40 my-2">
            <label className="flex flex-col items-center cursor-pointer">
              {backgroundImageFile || formData.backgroundImageUrl ? (
                <img
                  src={
                    backgroundImageFile
                      ? URL.createObjectURL(backgroundImageFile)
                      : formData.backgroundImageUrl
                  }
                  alt="Cover"
                  className="object-cover h-40 w-80 rounded-3xl"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-40 border-2 w-80 rounded-3xl border-primary">
                  <AddAPhotoIcon />
                  <p className="text-sm text-title font-montserrat_regular">
                    Add a cover photo
                  </p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(event) =>
                  handleImageUpload(event, "backgroundImage")
                }
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <CustomInput
            label="Speciality"
            inputType="text"
            CustomStyle="mb-5"
            value={formData.speciality}
            name="speciality"
            onChange={handleChange}
          />
          <CustomInput
            label="Level"
            inputType="text"
            CustomStyle="mb-5"
            value={formData.level}
            name="level"
            onChange={handleChange}
          />
          <div>
            <label
              className={`block text-xs font-montserrat_regular mb-2 text-title `}
            >
              Professur
            </label>
            <CustomAutocomplete
              options={teachersArray}
              value={formData.teacherId}
              onChange={(value) =>
                setFormData({ ...formData, teacherId: value })
              }
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-montserrat_regular mb-2 text-title">
            Groupes
          </label>
          <div className="flex flex-wrap gap-2">
            {availableGroups.length === 0 ? (
              <p className="text-sm text-text font-montserrat_regular">
                Aucun groupe disponible
              </p>
            ) : (
              availableGroups.map((group) => {
                const isSelected = selectedGroupIds.includes(group.value);
                return (
                  <button
                    key={group.value}
                    type="button"
                    onClick={() => handleGroupSelection(group.value)}
                    className={`px-4 py-2 rounded-lg border transition-colors duration-200 ${
                      isSelected
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-title border-border"
                    }`}
                  >
                    {group.label}
                  </button>
                );
              })
            )}
          </div>
          <p className="text-[11px] text-text mt-2">
            Choisissez un ou plusieurs groupes pour cette matiere (creation ou mise a jour).
          </p>
        </div>

        {/* Section UI removed as backend no longer supports it */}

        <div className="flex justify-center mt-6">
          <CustomButton text={buttonText} onClick={handleActionClick} />
        </div>
      </Box>
    </Modal>
  );
};

export default SubjectModal;
